<template>
  <div class="bui-entity-listing-page location-editor-redesign">
    <div class="location-editor-redesign__inner">
      <!-- Page header -->
      <header class="pe-header">
        <div class="pe-header__left">
          <div class="pe-header__title-row">
            <h1 class="bui-page-title">Location Editor</h1>
            <button
              type="button"
              class="pe-header__refresh"
              aria-label="Refresh location data"
              @click="loadLocation()"
            >
              <PhArrowsClockwise :size="22" weight="bold" />
            </button>
          </div>
          <div class="pe-header__subtitle">{{ values.name }}</div>
        </div>
      </header>

      <!-- Two-column editor grid -->
      <section class="editor-grid">
        <!-- LEFT COLUMN -->
        <div class="editor-col">
          <InfoCard>
            <template #title>
              <h2 class="info-card__title-text">Location Details</h2>
            </template>

            <StatusBar :errorMessage="errorMessage" />

            <div class="field-content">
              <div class="d-flex field-row">
                <label for="le-name" class="basis-1/2 text-sm font-bold">Name &#9733;</label>
                <TextField id="le-name" name="name" v-model="values.name" class="basis-1/2" width="100%" />
              </div>
              <div class="d-flex field-row">
                <label for="le-bu" class="basis-1/2 text-sm font-bold">Business Unit #</label>
                <TextField id="le-bu" name="businessUnitNumber" v-model="form.businessUnitNumber" class="basis-1/2" width="100%" />
              </div>
              <div class="d-flex field-row">
                <label for="le-address1" class="basis-1/2 text-sm font-bold">Address 1</label>
                <TextField id="le-address1" name="address1" v-model="form.address1" class="basis-1/2" width="100%" />
              </div>
              <div class="d-flex field-row">
                <label for="le-address2" class="basis-1/2 text-sm font-bold">Address 2</label>
                <TextField id="le-address2" name="address2" v-model="form.address2" class="basis-1/2" width="100%" />
              </div>
              <div class="d-flex field-row">
                <label for="le-city" class="basis-1/2 text-sm font-bold">City &#9733;</label>
                <TextField id="le-city" name="city" v-model="values.city" class="basis-1/2" width="100%" />
              </div>
              <div class="d-flex field-row">
                <label for="le-zip" class="basis-1/2 text-sm font-bold">Postal Code</label>
                <TextField id="le-zip" name="zip" v-model="form.zip" class="basis-1/2" width="100%" />
              </div>
              <div class="d-flex field-row">
                <label for="le-region" class="basis-1/2 text-sm font-bold">Region &#9733;</label>
                <div class="basis-1/2 bui-select">
                  <select id="le-region" name="region" v-model="values.region">
                    <option :value="region" v-for="region of regions" :key="region.name">
                      {{ region.name }}
                    </option>
                  </select>
                  <PhCaretDown :size="14" weight="bold" class="bui-select__caret" />
                </div>
              </div>
              <div class="d-flex field-row">
                <label for="le-country" class="basis-1/2 text-sm font-bold">Country &#9733;</label>
                <div class="basis-1/2 bui-select">
                  <select
                    id="le-country"
                    name="country"
                    v-model="values.country"
                    :disabled="values?.region?.countries?.length > 0 ? false : true"
                  >
                    <option :value="country" v-for="country of values?.region?.countries" :key="country.name">
                      {{ country.name }}
                    </option>
                  </select>
                  <PhCaretDown :size="14" weight="bold" class="bui-select__caret" />
                </div>
              </div>
              <div class="d-flex field-row">
                <label for="le-state" class="basis-1/2 text-sm font-bold">State</label>
                <div class="basis-1/2 bui-select">
                  <select
                    id="le-state"
                    name="state"
                    v-model="form.state"
                    :disabled="values?.country?.states?.length > 0 ? false : true"
                  >
                    <option :value="state" v-for="state of values?.country?.states" :key="state.name">
                      {{ state.name }}
                    </option>
                  </select>
                  <PhCaretDown :size="14" weight="bold" class="bui-select__caret" />
                </div>
              </div>
              <div class="d-flex field-row">
                <label for="le-timezone" class="basis-1/2 text-sm font-bold">Time Zone &#9733;</label>
                <div class="basis-1/2 bui-select">
                  <select id="le-timezone" name="timezone" v-model="values.timezone">
                    <option :value="zone.Id" v-for="zone of locationModel.timezoneOptions" :key="zone.Id">
                      {{ zone.DisplayName }}
                    </option>
                  </select>
                  <PhCaretDown :size="14" weight="bold" class="bui-select__caret" />
                </div>
              </div>
              <div class="d-flex field-row">
                <label for="le-contact" class="basis-1/2 text-sm font-bold">Contact Name</label>
                <TextField id="le-contact" name="contactName" v-model="form.contactName" class="basis-1/2" width="100%" />
              </div>
              <div class="d-flex field-row">
                <label for="le-phone" class="basis-1/2 text-sm font-bold">Phone</label>
                <TextField id="le-phone" name="phone" v-model="form.phoneNumber" class="basis-1/2" width="100%" />
              </div>
              <div class="d-flex field-row">
                <label for="le-hours" class="basis-1/2 text-sm font-bold">Hours</label>
                <TextField id="le-hours" name="hours" v-model="form.hours" class="basis-1/2" width="100%" />
              </div>
            </div>
          </InfoCard>
        </div>

        <!-- RIGHT COLUMN -->
        <div class="editor-col">
          <InfoCard>
            <template #title>
              <h2 class="info-card__title-text">Organization Information</h2>
            </template>

            <div class="field-content">
              <div class="d-flex field-row">
                <label class="basis-1/2 text-sm font-bold">Organization Name</label>
                <div class="basis-1/2 le-value">{{ organizationDetails.name }}</div>
              </div>
              <div class="d-flex field-row">
                <label class="basis-1/2 text-sm font-bold">Business Unit #</label>
                <div class="basis-1/2 le-value">{{ organizationDetails.number }}</div>
              </div>
            </div>
          </InfoCard>

          <InfoCard flush>
            <template #title>
              <h2 class="info-card__title-text">Players</h2>
            </template>

            <div class="le-empty" v-if="!(locationModel?.entities[0]?.players?.length > 0)">
              No players at this location.
            </div>
            <div class="pe-table-wrap" v-else>
              <table class="pe-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th class="pe-table__actions-col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="player of locationModel?.entities[0]?.players" :key="player.id">
                    <td>
                      <a class="campaign-link" @click="editPlayer(player.id)">{{ player.name }}</a>
                    </td>
                    <td class="pe-table__actions">
                      <button type="button" class="pe-row-action-btn" aria-label="Edit player"
                        @click="editPlayer(player.id)">
                        <PhPencilSimple :size="16" weight="bold" />
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="pe-table-footer" v-if="locationModel?.entities[0]?.players?.length > 0">
              Showing {{ locationModel.entities[0].players.length }} of {{ locationModel.entities[0].players.length }} player{{ locationModel.entities[0].players.length === 1 ? '' : 's' }}
            </div>
          </InfoCard>
        </div>
      </section>

      <!-- Sticky footer -->
      <footer class="editor-footer">
        <div class="editor-footer__inner">
          <ButtonCustomized label="Exit" v-if="!hasChanges" @click="onExit" ctaSecondary />
          <BeforeExitDialogue v-if="hasChanges" @save="submitAndExit" @exit="onExit" />
          <ButtonCustomized label="Save" cta @click="onSubmit" :disabled="!hasChanges" />
          <Saving ref="savingDialog" />
        </div>
      </footer>
    </div>
  </div>
</template>
<script lang="ts" setup>
import { onBeforeMount, onMounted, onUnmounted, ref, watchEffect } from 'vue'
import { PhArrowsClockwise, PhCaretDown, PhPencilSimple } from '@phosphor-icons/vue';
import TextField from "@/components/form-fields/TextField.vue";
import ButtonCustomized from "@/components/form-fields/ButtonCustomized.vue";
import StatusBar from "@/components/form-fields/StatusBar/StatusBar.vue";
import useNotifications from "@/components/Notifications/composables/notifications";
import router from '@/router';
import { onBeforeRouteLeave, useRoute } from 'vue-router'
import useLocationEditor from './LocationEditorHelper'
import BeforeExitDialogue from '@/components/Dialog/BeforeExitDialog/BeforeExitDialogue.vue'
import Saving from '@/components/Dialog/SavingDialog/Saving.vue';
import InfoCard from '@/components/bui/molecules/InfoCard/InfoCard.vue';

const savingDialog = ref()
const route = useRoute()
const notification = useNotifications();
const {  values, submitForm, isSuccess, loadLocation,form,regions,
  hasChanges, errorMessage, removeBeforeLoad, isLoading, locationId,locationModel,organizationDetails
} = useLocationEditor();
onBeforeMount(() => {
  locationId.value = route.params.id as string
  loadLocation()
})
onUnmounted(() => {
  removeBeforeLoad()
})
onBeforeRouteLeave((to, from) => {
  if (hasChanges.value) {
    const answer = window.confirm(
      'Changes you made may not be saved.'
    )
    if (!answer) return false
  }
})

async function submitAndExit() {
  savingDialog.value.open()
  await submitForm();
  savingDialog.value.close()
  if (isSuccess.value === true) {
    notification.success('Location updated successfully!');
    removeBeforeLoad();
    router.push({ path: '/locations' })
  }
}
async function onSubmit() {
 savingDialog.value.open()
  await submitForm();
  savingDialog.value.close()
  if (isSuccess.value === true) {
    removeBeforeLoad();
    notification.success('Location updated successfully!');
  }
}
function onExit() {
  removeBeforeLoad()
  router.push({ path: '/locations' })
}
function editPlayer (itemId:string){
  router.push({ name: 'player-editor', params: { id: itemId } });

}
</script>
<style lang="scss" scoped>
@import '@/assets/styles/bui-editor-template';

/* Outer wrapper: paints the BUI page surface edge-to-edge (see the note in
   PlayerEditorView — `.bui-entity-listing-page` is wired in PageLayout.vue). */
.location-editor-redesign {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100%;
  background: var(--bui-page-fill, #ff007b);
  color: var(--bui-page-head, #fffcfd);
  padding: var(--bui-page-pad-y, 32px) var(--bui-page-pad-x, 48px) 5rem;
  box-sizing: border-box;

  @media (max-width: 1100px) {
    padding-left: 24px;
    padding-right: 24px;
  }
}

.location-editor-redesign__inner {
  display: flex;
  flex-direction: column;
  gap: var(--bui-page-section-gap, 24px);
  width: 100%;
  flex: 1 1 auto;
  min-width: 0;
}

.le-value {
  font-size: 0.875rem;
  color: var(--bui-tbl-row-text, #2a2969);
}

.le-empty {
  padding: 0 1.25rem 1rem;
  color: var(--bui-tbl-detail-text, #5c5c66);
  font-size: 0.875rem;
}
</style>
